/**
 * Banner bloccanti sopra l'header del ticket. Il prototipo non ne ha nessuno
 * (SPEC §3.13): si reintroducono con il suo linguaggio (card 14px, badge mono,
 * niente ombre) perche sono funzioni dell'inventario §7.1 n.3 e n.4
 * (`TicketDetail.tsx:428-433` e `:497-527`).
 *
 * `isAdmin` arriva da `api.kind === 'admin'`, non da euristiche sui permessi:
 * la severita del secondo banner cambia perche l'admin PUO attribuire il
 * richiedente, mentre il rappresentante puo solo prenderne atto.
 */
import type { TicketDetail } from '../../../services/supportApi';

export interface S3Banner {
  id: 'merged' | 'needsRequester' | 'external' | 'participant' | 'copy';
  tone: 'warn' | 'dang' | 'info';
  title: string;
  body: string;
  actionLabel?: string;
}

export const buildBanners = (
  ticket: TicketDetail,
  ctx: { isAdmin: boolean; canSetRequester: boolean }
): S3Banner[] => {
  const out: S3Banner[] = [];

  if (ticket.mergedInto) {
    out.push({
      id: 'merged',
      tone: 'warn',
      title: 'Ticket unito a un altro',
      body: 'Questo ticket è stato unito: la lavorazione prosegue sul ticket superstite.',
      actionLabel: 'Apri il ticket superstite',
    });
  }

  // `inbound` esiste solo sui ticket nati dalla casella di supporto: su ogni
  // altra origine il campo, se c'e, e un residuo e non deve produrre avvisi
  // (parita con TicketDetail.tsx:383, dove il box e gated su origin==='email').
  if (ticket.origin === 'email' && ticket.inbound?.needsRequester) {
    out.push({
      id: 'needsRequester',
      tone: ctx.isAdmin ? 'dang' : 'warn',
      // Chi non puo attribuire (area rappresentante) deve sapere a chi
      // rivolgersi (parita TicketDetail.tsx:509-511).
      title: ctx.isAdmin
        ? 'Richiedente da attribuire'
        : 'Richiedente da attribuire: contatta un amministratore',
      body: 'Il mittente della email non è stato riconosciuto: le notifiche al richiedente sono sospese finché non lo attribuisci.',
      ...(ctx.canSetRequester && { actionLabel: 'Imposta richiedente' }),
    });
  }

  // Canale in copia (parte A): legge, scrive note, propone bozze che il
  // responsabile approva.
  if (ticket.viewerRole === 'copy') {
    out.push({
      id: 'copy',
      tone: 'info',
      title: 'Ticket condiviso in copia con il tuo canale',
      body: 'Il tuo canale è in copia su questo ticket: puoi leggere, scrivere note interne e proporre risposte che il canale responsabile approverà.',
    });
  } else if (ticket.viewerRole === 'participant') {
    // Partecipante invitato (parte B): legge, scrive note, propone bozze.
    out.push({
      id: 'participant',
      tone: 'info',
      title: 'Sei stato invitato in questo ticket',
      body: 'Puoi leggere, scrivere note interne e proporre risposte che il canale approverà.',
    });
  } else if (ticket.viewerIsMember === false) {
    // Assegnatario esterno al canale (Task 26): avviso discreto, nessuna azione.
    const canale = ticket.groupId?.nome;
    out.push({
      id: 'external',
      tone: 'info',
      title: canale
        ? `Sei assegnato a questo ticket come esterno al canale ${canale}`
        : 'Sei assegnato a questo ticket come esterno al canale',
      body: 'Puoi rispondere, scrivere note e cambiare lo stato. Trasferimento, unione, chiusura e componenti restano ai componenti del canale.',
    });
  }

  return out;
};
